"use client";

import * as XLSX from "xlsx-js-style";
import type { NormalizedPropertyRecord } from "@/services/properties-api";

/** Fill style for parcel data columns so they are visually distinct in Excel. */
const PARCEL_COLUMN_FILL = {
	patternType: "solid" as const,
	fgColor: { rgb: "FFE8F4E8" },
	bgColor: { rgb: "FFE8F4E8" },
};
import { toast } from "sonner";
import { exportToS3 } from "@/services/exports-api";

/**
 * Format a value for CSV/Excel export. Unwraps JSON objects and strings that
 * contain address/value/name/text so "Parcel Original Address" etc. export as
 * plain text, not as JSON.
 */
function formatValueForExport(value: unknown): string {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") {
		const obj = value as Record<string, unknown>;
		if (typeof obj.address === "string") return obj.address;
		if (typeof obj.value === "string") return obj.value;
		if (typeof obj.name === "string") return obj.name;
		if (typeof obj.text === "string") return obj.text;
		try {
			return JSON.stringify(value);
		} catch {
			return String(value);
		}
	}
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
			try {
				const parsed = JSON.parse(trimmed) as Record<string, unknown>;
				if (typeof parsed.address === "string") return parsed.address;
				if (typeof parsed.value === "string") return parsed.value;
				if (typeof parsed.name === "string") return parsed.name;
				if (typeof parsed.text === "string") return parsed.text;
			} catch {
				// not valid JSON, use as-is
			}
		}
		return value;
	}
	return String(value);
}

/**
 * Export property records to Excel (.xlsx). Same logic as dashboard ExportButtons.
 * Uses toasts for progress; uploads to S3 and triggers download.
 */
export async function exportPropertiesToExcel(
	rowsToExport: NormalizedPropertyRecord[]
): Promise<void> {
	if (rowsToExport.length === 0) {
		toast.error("No properties selected for export");
		return;
	}

	const displayNames = rowsToExport[0]?._displayNames;
	if (!displayNames) {
		toast.error("Unable to determine column structure");
		return;
	}

	const progressToast = toast.loading(
		`Preparing export of ${rowsToExport.length} propert${rowsToExport.length === 1 ? "y" : "ies"}...`
	);

	try {
		const columnDefs = Object.entries(displayNames)
			.filter(([key]) => key !== "_displayNames")
			.map(([key, displayName]) => ({
				id: key,
				header: displayName || key,
			}));

		const excelData: Record<string, unknown>[] = [];
		const batchSize = 100;

		for (let i = 0; i < rowsToExport.length; i += batchSize) {
			const batch = rowsToExport.slice(i, i + batchSize);
			batch.forEach((property) => {
				const row: Record<string, unknown> = {};
				columnDefs.forEach((colDef) => {
					if (colDef.id === "parcelData") return;
					const value = (property as Record<string, unknown>)[
						colDef.id
					];
					row[colDef.header] = formatValueForExport(value);
				});
				if (property.parcelData) {
					const parcel = property.parcelData as Record<
						string,
						unknown
					>;
					Object.entries(parcel).forEach(([key, value]) => {
						const formattedKey = key
							.split(/(?=[A-Z])|_/)
							.map(
								(word) =>
									word.charAt(0).toUpperCase() +
									word.slice(1).toLowerCase()
							)
							.join(" ");
						row[`Parcel ${formattedKey}`] =
							formatValueForExport(value);
					});
				}
				excelData.push(row);
			});
			const progress = Math.min(
				100,
				((i + batch.length) / rowsToExport.length) * 100
			);
			toast.loading(
				`Exporting... ${Math.round(progress)}% (${i + batch.length}/${rowsToExport.length})`,
				{ id: progressToast }
			);
			await new Promise((resolve) => setTimeout(resolve, 10));
		}

		toast.loading("Generating Excel file...", { id: progressToast });

		const worksheet = XLSX.utils.json_to_sheet(excelData);
		// Identify parcel columns (headers starting with "Parcel ") and apply distinct fill
		const parcelCols = new Set<string>();
		for (const addr of Object.keys(worksheet)) {
			if (addr.startsWith("!")) continue;
			const cell = worksheet[addr];
			if (!cell?.v) continue;
			const rowNum = addr.replace(/^[A-Z]+/, "");
			if (rowNum !== "1") continue;
			if (String(cell.v).startsWith("Parcel ")) {
				parcelCols.add(addr.replace(/\d+$/, ""));
			}
		}
		for (const addr of Object.keys(worksheet)) {
			if (addr.startsWith("!")) continue;
			const colLetter = addr.replace(/\d+$/, "");
			if (!parcelCols.has(colLetter)) continue;
			const cell = worksheet[addr];
			if (!cell) continue;
			(cell as { s?: { fill?: unknown } }).s = {
				...(typeof (cell as { s?: Record<string, unknown> }).s ===
					"object" && (cell as { s?: Record<string, unknown> }).s),
				fill: PARCEL_COLUMN_FILL,
			};
		}
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Properties");
		const excelBuffer = XLSX.write(workbook, {
			bookType: "xlsx",
			type: "array",
		});
		const blob = new Blob([excelBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});

		toast.loading("Uploading to cloud storage...", { id: progressToast });
		try {
			await exportToS3("Raw", blob, "xlsx");
		} catch (s3Error) {
			console.error("Failed to upload to S3:", s3Error);
			toast.warning("File saved locally, but cloud upload failed", {
				id: progressToast,
			});
		}

		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = `properties-${new Date().toISOString().split("T")[0]}.xlsx`;
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(link.href);

		toast.success(
			`Exported ${rowsToExport.length} propert${rowsToExport.length === 1 ? "y" : "ies"} to Excel`,
			{ id: progressToast }
		);
	} catch (error) {
		console.error("Export error:", error);
		toast.error("Failed to export properties. Please try again.");
	}
}

/**
 * Export property records to Propstream CSV. Same logic as dashboard ExportButtons.
 */
export async function exportPropertiesToPropstream(
	rowsToExport: NormalizedPropertyRecord[]
): Promise<void> {
	if (rowsToExport.length === 0) {
		toast.error("No properties selected for export");
		return;
	}

	const progressToast = toast.loading(
		`Preparing export of ${rowsToExport.length} propert${rowsToExport.length === 1 ? "y" : "ies"}...`
	);

	try {
		const exportableData = rowsToExport.map((value) => {
			const newValue: Record<string, string> = {};
			const parcelData = value.parcelData as
				| Record<string, unknown>
				| undefined;

			let owner = value.owner || "";
			if (!owner && (value as Record<string, unknown>).previous_owner) {
				owner = String(
					(value as Record<string, unknown>).previous_owner
				);
			}
			if (!owner && parcelData) {
				owner = (parcelData.owner as string) || "";
			}
			if (owner) {
				const ownerNameSplit = owner.split(" ");
				newValue["First Name"] = ownerNameSplit[0] || "";
				newValue["Last Name"] = ownerNameSplit.slice(1).join(" ") || "";
			} else {
				newValue["First Name"] = "";
				newValue["Last Name"] = "";
			}

			newValue["Phone"] = "";
			newValue["Email"] = "";
			newValue["Mobile Phone"] = "";
			newValue["Landline"] = "";

			let address = value.address || "";
			if (
				!address &&
				(value as Record<string, unknown>).physical_address
			) {
				address = String(
					(value as Record<string, unknown>).physical_address
				);
			}
			if (!address && parcelData) {
				address = (parcelData.address as string) || "";
			}
			newValue["Street Address"] = address;

			let zip = value.zip || "";
			if (!zip && parcelData) zip = (parcelData.szip5 as string) || "";
			newValue["Zip"] = zip;
			newValue["Mail Zip"] = zip;

			let state = value.region || "";
			if (!state && parcelData)
				state = (parcelData.state2 as string) || "";
			newValue["State"] = state;
			newValue["Mail State"] = state;

			let city = "";
			if (parcelData) city = (parcelData.scity as string) || "";
			newValue["City"] = city;
			newValue["Mail City"] = city;

			let mailingAddress = address;
			if ((value as Record<string, unknown>).mailing_address) {
				mailingAddress = String(
					(value as Record<string, unknown>).mailing_address
				);
			}
			if (!mailingAddress && parcelData) {
				mailingAddress =
					(parcelData.mailadd as string) || mailingAddress;
			}
			newValue["Mail Street Address"] = mailingAddress;

			if (parcelData) {
				newValue["Mail City"] =
					(parcelData.mail_city as string) || newValue["Mail City"];
				newValue["Mail State"] =
					(parcelData.mail_state2 as string) ||
					newValue["Mail State"];
				newValue["Mail Zip"] =
					(parcelData.mail_zip as string) || newValue["Mail Zip"];
			}

			newValue["Mail Address Same"] = "Yes";
			newValue["Type"] = "Owner";
			newValue["Status"] = "New";
			return newValue;
		});

		toast.loading("Generating CSV file...", { id: progressToast });

		const headers = [
			"First Name",
			"Last Name",
			"Phone",
			"Email",
			"Mobile Phone",
			"Landline",
			"Street Address",
			"Zip",
			"State",
			"City",
			"Mail Street Address",
			"Mail Zip",
			"Mail State",
			"Mail City",
			"Mail Address Same",
			"Type",
			"Status",
		];
		const escapeCSV = (v: string | number | null | undefined) => {
			if (v === null || v === undefined) return "";
			const str = String(v);
			if (str.includes(",") || str.includes('"') || str.includes("\n")) {
				return `"${str.replace(/"/g, '""')}"`;
			}
			return str;
		};
		const csvRows = [headers.join(",")];
		exportableData.forEach((row) => {
			csvRows.push(
				headers.map((header) => escapeCSV(row[header] || "")).join(",")
			);
		});
		const blob = new Blob([csvRows.join("\n")], {
			type: "text/csv;charset=utf-8;",
		});

		toast.loading("Uploading to cloud storage...", { id: progressToast });
		try {
			await exportToS3("Propstream", blob, "csv");
		} catch (s3Error) {
			console.error("Failed to upload to S3:", s3Error);
			toast.warning("File saved locally, but cloud upload failed", {
				id: progressToast,
			});
		}

		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = `properties-propstream-${new Date().toISOString().split("T")[0]}.csv`;
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(link.href);

		toast.success(
			`Exported ${rowsToExport.length} propert${rowsToExport.length === 1 ? "y" : "ies"} to Propstream CSV`,
			{ id: progressToast }
		);
	} catch (error) {
		console.error("Export error:", error);
		toast.error("Failed to export properties. Please try again.");
	}
}
